import { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Button from "components/Buttons/Button/Button";
import Loading from "components/Loading/Loading";
import ButtonCloseModal from "components/Buttons/ButtonCloseModal/ButtonCloseModal";
import useSnackbar from "hooks/useSnackbar";
import AccountService from "services/AccountService";
import { Color } from "enums/Common";
import { formatDate } from "helpers/CommonHelper";
import {
  IAccountDeactivationInfo,
  IAccountDirectory,
} from "interfaces/Account";

interface DeactivateAccountModalProps {
  account: IAccountDirectory;
  onClose: () => void;
  onSuccess: () => void;
}

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

const DeactivateAccountModal = (props: DeactivateAccountModalProps) => {
  const { account, onClose, onSuccess } = props;

  const { snackbarComponent, setSnackbarError } = useSnackbar();

  const [info, setInfo] = useState<IAccountDeactivationInfo>();
  const [reviewed, setReviewed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    AccountService.getDeactivationInfo(account.id)
      .then(({ data }) => setInfo(data))
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  }, [account.id, setSnackbarError]);

  const orders = info?.orders ?? [];
  const sensors = info?.sensors ?? [];
  const hasImpact = orders.length > 0 || sensors.length > 0;

  const handleSubmit = () => {
    setLoading(true);
    AccountService.edit(account.id, { isInactive: true })
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch(() => {
        setSnackbarError();
        setLoading(false);
      });
  };

  const listClassName = "max-h-[200px] overflow-y-auto";
  const rowClassName =
    "flex justify-between gap-3 px-4 py-2 text-sm border-t border-pattensBlue2/60 first:border-t-0";
  const headerClassName =
    "flex justify-between items-center gap-3 px-4 py-2.5 bg-aliceBlue3 border-b border-pattensBlue2 text-sm font-bold text-cloudBurst";

  return (
    <Modal open onClose={onClose}>
      <>
        <div className="absolute w-4/5 max-w-[660px] max-h-[90vh] overflow-y-auto flex flex-col top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md shadow-2xl bg-white px-8 py-7">
          <ButtonCloseModal
            className="!top-5"
            color={Color.CloudBurst}
            onClick={onClose}
          />

          <h1 className="text-cloudBurst text-lg font-bold mb-1 pr-8">
            Deactivate {account.name}?
          </h1>

          {info && (
            <>
              <p className="text-sm mb-4">
                {hasImpact
                  ? "Deactivating closes the business relationship. Review the orders and sensors below before you continue."
                  : "This account has no open orders and no sensors out. Users will no longer be able to sign in."}
              </p>

              {orders.length > 0 && (
                <div className="rounded-md border border-pattensBlue2 mb-3">
                  <div className={headerClassName}>
                    {plural(orders.length, "order")} will be cancelled
                    <span className="font-normal text-fiord/70 text-xs">
                      current billing cycle is the last
                    </span>
                  </div>
                  <ul className={listClassName}>
                    {orders.map((order) => (
                      <li key={order.id} className={rowClassName}>
                        <span>
                          <b>{order.orderNumber}</b>
                          {order.patient && ` · ${order.patient}`}
                        </span>
                        {order.voidRequested ? (
                          <span className="rounded-full bg-[#FDF3DC] px-2 py-0.5 text-xs font-bold text-[#975A16] whitespace-nowrap">
                            Void requested
                          </span>
                        ) : (
                          <span className="text-fiord/70 text-right">
                            {order.orderType}
                            {order.endDate &&
                              ` · ends ${formatDate(order.endDate)}`}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {sensors.length > 0 && (
                <div className="rounded-md border border-pattensBlue2 mb-3">
                  <div className={headerClassName}>
                    {plural(sensors.length, "sensor")} still out with patients
                    or the account
                    <span className="font-normal text-fiord/70 text-xs">
                      must be collected
                    </span>
                  </div>
                  <ul className={listClassName}>
                    {sensors.map((sensor) => (
                      <li key={sensor.name} className={rowClassName}>
                        <span>
                          <b>{sensor.name}</b>
                          {sensor.holder && ` · ${sensor.holder}`}
                          {sensor.isAccountUnit && (
                            <span className="text-fiord/70"> (account unit)</span>
                          )}
                        </span>
                        <span className="text-fiord/70 text-right">
                          {sensor.status}
                          {sensor.statusOn && ` ${formatDate(sensor.statusOn)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasImpact && (
                <>
                  <p className="mt-1 mb-4 border-l-[3px] border-crimson bg-crimson/5 px-3 py-2 text-sm">
                    Orders are cancelled immediately and stay cancelled if the
                    account is reactivated. BioMech operations will receive an
                    email with the orders and sensors to collect.
                  </p>

                  <label className="flex items-center gap-2 text-sm mb-1 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-pelorous"
                      checked={reviewed}
                      onChange={(e) => setReviewed(e.target.checked)}
                    />
                    I have reviewed these orders and sensors.
                  </label>
                </>
              )}

              <div className="flex gap-5 justify-end mt-5">
                <Button
                  className="border-2 border-pelorous !bg-white !text-pelorous"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  className="!bg-crimson"
                  disabled={hasImpact && !reviewed}
                  onClick={handleSubmit}
                >
                  Deactivate Account
                </Button>
              </div>
            </>
          )}

          <Loading loading={loading} />
        </div>

        {snackbarComponent}
      </>
    </Modal>
  );
};

export default DeactivateAccountModal;
